import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  DeliveryStoreService,
  ExceptionResolution,
  MyOrdersStoreService,
  Order,
  OrderException,
  OrderExceptionStoreService,
  OrderHeader,
  OrderItem,
  OrderStatus,
  ResolvedOrderItem,
} from '@usf/ngrx-order';
import { ProductSummary } from '@usf/product-types';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { CustomerStoreService } from 'src/app/ngrx-customer/services';
import { ProductAlternativeSearchService } from 'src/app/product-detail/services/product-alternative-search.service';
import {
  ErrorType,
  ExceptionType,
} from '../constants/order-exception-type.enum';
import { ResolutionType } from '../constants/resolution-type.enum';
import { EmittedFlippedProductNumber } from '../models/emitted-flipped-product-number.model';
import { EmittedPartialFlag } from '../models/emitted-partial-flag-selection.model';
import { OrderExceptionViewModel } from '../models/order-exception-view.model';
import {
  selectExceptionResolutionWithId,
  selectOrderExceptionViewModel,
  selectProductNumbersFromException,
} from '../selectors/order-exception-view-model.selectors';
import * as dayjs from 'dayjs';
import { OrderExceptionsAnalyticsService } from '../tracking/analytics/order-exceptions-analytics.service';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { NavigationHelperService } from 'src/app/shared/helpers/navigation.helpers.service';
import { OrderExceptionsTracingService } from '../tracking/tracing/order-exceptions-tracing.service';
import { PATHS } from '@shared/constants/paths';
import { ProductCompareEnum } from '@shared/constants/product-compare.enum';
import {
  AnalyticsProduct,
  AnalyticsSubstituteProduct,
} from '@shared/models/tracking/analytics/analytics-product';
import { ToggleNewSearchService } from '@shared/services/toggle-new-search/toggle-new-search.service';
import {
  MultiExceptionResolved,
  getExceptionType,
  shouldRemoveSubbedOutOrderItem as shouldRemoveSubbedOutOrderItem,
} from '../utils/order-exception.util';
import { PUNCHOUT_PO_PENDING } from '@shared/constants/order-punchout';
import { OrderInsightsService } from '@order/services/order-insights.service';
import { OrderService } from '@order/services/order.service';
import { ModalController } from '@ionic/angular';
import { ModalService } from '@shared/services/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { StayOnPageModalComponent } from '@shared/components/stay-on-page-modal/stay-on-page-modal.component';
@Injectable({
  providedIn: 'root',
})
export class OrderExceptionService {
  constructor(
    private store: Store,
    private router: Router,
    private orderExceptionStoreService: OrderExceptionStoreService,
    private productAlternativeSearchService: ProductAlternativeSearchService,
    private customerStoreService: CustomerStoreService,
    private deliveryStoreService: DeliveryStoreService,
    private myOrdersStoreService: MyOrdersStoreService,
    private orderExceptionsAnalyticsService: OrderExceptionsAnalyticsService,
    private panAppState: PanAppState,
    private navigationHelperService: NavigationHelperService,
    private orderExceptionsTracingService: OrderExceptionsTracingService,
    private orderInsightsService: OrderInsightsService,
    private orderService: OrderService,
    private readonly toggleNewSearchService: ToggleNewSearchService,
    private modalController: ModalController,
    private modalService: ModalService,
    private translateService: TranslateService,
    readonly platformService: PlatformService,
  ) {}

  subject = new Subject<boolean>();

  static getProductNumberForExceptionType(
    exceptionType: ExceptionType,
    orderExceptions: Map<ExceptionType, ExceptionResolution[]>,
  ): number[] {
    const resolutions = orderExceptions.get(exceptionType).values();
    const productNumbers: number[] = Array.from(resolutions).map(
      res => res.orderItem.productNumber,
    );
    return productNumbers;
  }

  static determineDeliveryDate(
    requestedDeliveryDate: string | Date,
    nextDeliveryDate: string,
    resolutionDate: string,
  ): string {
    if (!!resolutionDate) {
      return resolutionDate;
    }
    let deliveryDate: string;
    if (
      nextDeliveryDate &&
      dayjs(requestedDeliveryDate).diff(nextDeliveryDate, 'day') <= 0
    ) {
      deliveryDate = dayjs(nextDeliveryDate).utc(false).toISOString();
    } else {
      deliveryDate = dayjs(requestedDeliveryDate).utc(false).toISOString();
    }
    return deliveryDate;
  }

  getOrderExceptionViewModel(): Observable<OrderExceptionViewModel> {
    const orderExceptionVM$ = this.store.select(selectOrderExceptionViewModel);
    const deliveryDays$ =
      this.customerStoreService.selectedCustomerDeliveryDays();
    const deliveryInfo$ =
      this.deliveryStoreService.getNextDeliveryInformation();

    return combineLatest([
      orderExceptionVM$,
      deliveryDays$,
      deliveryInfo$,
    ]).pipe(
      map(([vm, days, info]) => {
        vm.deliveryDays = days;
        vm.nextDeliveryDate = info;
        return vm;
      }),
    );
  }

  getOrderWithId(orderId: string): Observable<Order> {
    return this.myOrdersStoreService.getOrderWithId(orderId);
  }

  setExceptionsForOrderId(orderId: string) {
    if (orderId) {
      this.getOrderWithId(orderId)
        .pipe(
          filter(order => !!order),
          take(1),
        )
        .subscribe(order => {
          this.setExceptionsForOrder(order);
        });
    }
  }

  setExceptionsForOrder(order: Order) {
    const exceptionResolutions = order.orderHeader.orderExceptions.map(
      exception => {
        const orderItem = order.orderItems.find(
          item => item.productNumber === exception.productNumber,
        );
        const exceptionResolution: ExceptionResolution = {
          orderItem: orderItem,
          orderException: exception,
          expanded: true,
        } as ExceptionResolution;

        return exceptionResolution;
      },
    );
    this.orderExceptionStoreService.setOrderExceptions(
      order,
      exceptionResolutions,
    );
  }

  getOrderExceptionProductNumbers(): Observable<number[]> {
    return this.store.select(selectProductNumbersFromException);
  }

  clearOrderExceptions() {
    this.orderExceptionStoreService.clearOrderExceptions();
  }

  navigateToResolveExceptionsPage(
    orderHeader: OrderHeader,
    isConfirmationFlow = false,
  ) {
    const orderId = `${orderHeader.customerNumber}:${orderHeader.departmentNumber}:${orderHeader.orderId}`;
    const currentUrl = this.router?.routerState?.snapshot?.url;
    this.orderExceptionsTracingService.processViewExceptionsLinkClick(
      orderHeader,
      currentUrl,
    );
    this.setIsConfirmationFlow(isConfirmationFlow);
    this.router.navigate(['order', 'order-exceptions', orderId]);
  }

  resolveException(
    exceptionResolution: ExceptionResolution,
    resolutionType: ResolutionType,
    subOrderItem: OrderItem,
  ) {
    const copyResolution = { ...exceptionResolution };
    if (
      resolutionType !== ResolutionType.DATE &&
      resolutionType !== ResolutionType.UNUSUAL_QUANTITY
    ) {
      copyResolution.expanded = false;
    }
    let resolvedOrderItem: ResolvedOrderItem = {
      originalProduct: null,
      substituteProduct: null,
    };

    const exceptionType = getExceptionType(
      exceptionResolution.orderException.errorType,
      exceptionResolution.orderException.errorCode,
    );
    switch (resolutionType) {
      case ResolutionType.ACKNOWLEDGED:
        resolvedOrderItem = {
          originalProduct: exceptionResolution.orderItem,
          substituteProduct: null,
        };
        copyResolution.resolutionOrderItem = resolvedOrderItem;
        break;

      case ResolutionType.WAIT_FOR_RESTOCK:
      case ResolutionType.ADD_TO_WAITLIST:
        resolvedOrderItem = {
          originalProduct: {
            ...exceptionResolution.orderItem,
            substituteFlag: 'W',
            substituteFor: 0,
          },
          substituteProduct: null,
        };
        copyResolution.resolutionOrderItem = resolvedOrderItem;
        break;

      case ResolutionType.SUBSTITUTE:
        resolvedOrderItem = copyResolution.resolutionOrderItem =
          this.resolveFullSubstitute(
            exceptionResolution,
            subOrderItem,
            exceptionType,
          );
        break;

      case ResolutionType.REMOVE:
        resolvedOrderItem = {
          originalProduct: {
            ...exceptionResolution.orderItem,
            unitsOrdered: {
              currentValue: 0,
              previousValue: 0,
              loading: false,
              error: null,
            },
            eachesOrdered: {
              currentValue: 0,
              previousValue: 0,
              loading: false,
              error: null,
            },
            dirtyFlag: true,
          },
          substituteProduct: null,
        };
        copyResolution.resolutionOrderItem = resolvedOrderItem;
        if (
          exceptionType === ExceptionType.OUT_OF_STOCK &&
          resolutionType === ResolutionType.REMOVE
        ) {
          const orderItem = exceptionResolution.orderItem;
          combineLatest([
            this.orderService.getProductsForTracing$([orderItem]),
            this.getOrderExceptionViewModel(),
          ])
            .pipe(take(1))
            .subscribe(([productMap, vm]) => {
              this.orderExceptionsAnalyticsService.trackRemoveOOSProduct(
                productMap,
                vm,
              );
            });
        }
        break;

      case ResolutionType.UNUSUAL_QUANTITY:
        copyResolution.resolutionOrderItem =
          exceptionResolution.resolutionOrderItem;
        break;

      case ResolutionType.PRICE:
        copyResolution.expanded = exceptionResolution.expanded;
        copyResolution.resolutionOrderItem =
          exceptionResolution.resolutionOrderItem;
        break;
      case ResolutionType.DATE:
        resolvedOrderItem = {
          originalProduct: exceptionResolution.orderItem,
          substituteProduct: null,
        };
        copyResolution.resolutionOrderItem = resolvedOrderItem;
        break;

      default:
        break;
    }
    copyResolution.updateTimeStamp = new Date().toString();
    copyResolution.resolutionType = this.collapsedExceptionStatus(
      resolutionType,
      exceptionType,
    );
    this.orderExceptionStoreService.resolveException(copyResolution);
  }

  updateExpanded(val, exceptionType) {
    this.orderExceptionStoreService.expandException(
      val,
      ExceptionType[exceptionType],
    );
  }
  /**
   * Applies all exception resolutions to an order then dispatch action to backend persist
   * @param vm contains all the data needed to create an resolved order
   */
  resolveOrder(vm: OrderExceptionViewModel): void {
    let divisionNumber: number;
    // Get the current users division
    this.panAppState.customer$
      .pipe(take(1))
      .subscribe(cust => (divisionNumber = cust.divisionNumber));
    this.orderExceptionsAnalyticsService.submitOrder(
      vm.order,
      vm.orderExceptions,
      vm.productMap,
      divisionNumber,
    );

    const order: Order = JSON.parse(JSON.stringify(vm.order));
    order.orderHeader.orderStatus = OrderStatus.EDITING_SUBMITTED;
    if (order.orderHeader.purchaseOrderNumber === PUNCHOUT_PO_PENDING) {
      order.orderHeader.purchaseOrderNumber = undefined;
    }
    const exceptionMapByProductNum: Map<number, MultiExceptionResolved[]> =
      new Map();
    order.orderHeader.updateDtm = new Date().toISOString();
    for (const exceptionResolutions of vm.orderExceptions.values()) {
      for (const exceptionResolution of exceptionResolutions) {
        if (
          exceptionResolution.orderException.errorType !==
            ErrorType.ACKNOWLEDGEMENT &&
          exceptionResolution.orderException.errorType !== ErrorType.DATE
        ) {
          if (exceptionResolution.hasOwnProperty('resolutionOrderItem')) {
            if (
              exceptionResolution.resolutionOrderItem.substituteProduct
                ?.productNumber
            ) {
              exceptionMapByProductNum.set(
                exceptionResolution.resolutionOrderItem.substituteProduct
                  .productNumber,
                [
                  {
                    orderException: exceptionResolution.orderException,
                    orderItemIndexInOrder: -1,
                    resolvedItem:
                      exceptionResolution.resolutionOrderItem.substituteProduct,
                    updateTimeStamp: new Date().toString(),
                  },
                ],
              );
            }
            const orderItemIndex = order?.orderItems?.findIndex(
              orderItem =>
                orderItem?.productNumber ===
                exceptionResolution?.resolutionOrderItem.originalProduct
                  ?.productNumber,
            );
            let multiExceptionResolved: MultiExceptionResolved = {
              orderException: exceptionResolution.orderException,
              orderItemIndexInOrder: orderItemIndex,
              resolvedItem:
                exceptionResolution?.resolutionOrderItem?.originalProduct,
              updateTimeStamp: exceptionResolution?.updateTimeStamp,
            };
            if (
              exceptionMapByProductNum.has(
                order?.orderItems[orderItemIndex]?.productNumber,
              )
            ) {
              exceptionMapByProductNum.set(
                order?.orderItems[orderItemIndex]?.productNumber,
                [
                  ...exceptionMapByProductNum?.get(
                    order?.orderItems[orderItemIndex]?.productNumber,
                  ),
                  multiExceptionResolved,
                ],
              );
            } else {
              if (order?.orderItems[orderItemIndex]?.productNumber) {
                exceptionMapByProductNum.set(
                  order.orderItems[orderItemIndex].productNumber,
                  [multiExceptionResolved],
                );
              }
            }
          }
        } else if (
          exceptionResolution.orderException.errorType == ErrorType.DATE
        ) {
          const highestPriorityResolution = vm.orderExceptions.get(
            ExceptionType.DATE,
          )[0];
          order.orderHeader.requestedDeliveryDate =
            highestPriorityResolution.resolutionDate;
        }
      }
    }

    order.orderHeader.orderExceptions = this.getResolvedOrderExceptions(
      vm.orderExceptions,
    );
    this.handleMultipleExceptionInSameProduct(exceptionMapByProductNum, order);
  }

  handleOrderHasNoExceptions(orderId: string) {
    this.navigationHelperService.routeToSubmittedOrder(orderId, true);
  }

  /**
   * Recreates the orderExceptions array based on the exceptions resolved by the user.
   * @param orderExceptions our domain mappping of type, and resolution
   * @returns api domain OrderException array
   * Note that private methods don't have unit tests called directly from specs,
   * Instead they are tested in the exposed methods that consume them.
   */
  private getResolvedOrderExceptions(
    orderExceptions: Map<ExceptionType, ExceptionResolution[]>,
  ): OrderException[] {
    const resolvedOrderExceptions: OrderException[] = [];
    for (const exceptionResolutions of orderExceptions.values()) {
      for (const exceptionResolution of exceptionResolutions) {
        if (exceptionResolution.resolutionOrderItem) {
          resolvedOrderExceptions.push(exceptionResolution.orderException);
        }
      }
    }
    return resolvedOrderExceptions;
  }
  //use only for resolveOrder() scope
  handleMultipleExceptionInSameProduct(
    exceptionMapByProductNum: Map<number, MultiExceptionResolved[]>,
    order: Order,
  ) {
    let indexOfLastUpdatedQtyExceptionItem;
    for (let [key, value] of exceptionMapByProductNum) {
      if (value.length === 1) {
        if (value[0]?.orderItemIndexInOrder === -1) {
          order.orderItems.push(value[0].resolvedItem);
        } else {
          order.orderItems[value[0].orderItemIndexInOrder] =
            value[0]?.resolvedItem;
        }
      } else if (value.length > 1) {
        let lastUpdatedUUQtyResolutionDate = Math.max.apply(
          Math,
          value
            .filter(f => f?.orderException.errorType === ErrorType.QUANTITY)
            .map(qtyException =>
              new Date(qtyException?.updateTimeStamp).getTime(),
            ),
        );
        indexOfLastUpdatedQtyExceptionItem = value.findIndex(
          val =>
            val.updateTimeStamp ===
            new Date(lastUpdatedUUQtyResolutionDate).toString(),
        );
        const unAvailableException = value.find(
          x =>
            x?.orderException.errorCode === 607 ||
            x?.orderException.errorCode === 702,
        );
        const outOfStockException = value.find(
          x => x?.orderException.errorCode === 701,
        );
        const priceException = value.find(
          x =>
            x?.orderException.errorCode === 501 ||
            x?.orderException.errorCode === 502,
        );
        const qtyException = value.find(
          x => x?.orderException.errorType === 'QTY',
        );
        value.forEach(entry => {
          if (unAvailableException || outOfStockException) {
            order.orderItems[entry.orderItemIndexInOrder] = entry.resolvedItem;
          } else if (
            entry.orderException.errorType === 'QTY' ||
            entry.orderException.errorType === 'PRICE'
          ) {
            if (priceException && qtyException) {
              //merge QTY from last updated qtyException item to priceException item
              const updateQtyToPriceExceptionItem = {
                ...priceException.resolvedItem,
                eachesOrdered:
                  value[indexOfLastUpdatedQtyExceptionItem]?.resolvedItem
                    ?.eachesOrdered,
                unitsOrdered:
                  value[indexOfLastUpdatedQtyExceptionItem]?.resolvedItem
                    ?.unitsOrdered,
                unitsReserved:
                  value[indexOfLastUpdatedQtyExceptionItem]?.resolvedItem
                    ?.unitsReserved,
                eachesReserved:
                  value[indexOfLastUpdatedQtyExceptionItem]?.resolvedItem
                    ?.eachesReserved,
              };
              order.orderItems[entry.orderItemIndexInOrder] =
                updateQtyToPriceExceptionItem;
            } else if (qtyException) {
              //qtyException.(No price exception present)
              order.orderItems[entry?.orderItemIndexInOrder] =
                exceptionMapByProductNum.get(key)[
                  indexOfLastUpdatedQtyExceptionItem
                ].resolvedItem;
            } else if (priceException) {
              order.orderItems[entry?.orderItemIndexInOrder] =
                entry.resolvedItem;
            }
          } else {
            order.orderItems[entry.orderItemIndexInOrder] = entry?.resolvedItem;
          }
        });
        // If product has either a qty or unavail and oos exception, take the oos resolution
        if ((qtyException || unAvailableException) && outOfStockException) {
          order.orderItems[outOfStockException.orderItemIndexInOrder] = {
            ...outOfStockException.resolvedItem,
            dirtyFlag: true,
          };
        }
      }
    }
    return this.orderInsightsService.shouldIncludeInsightsOnOrderSubmission(
      order?.orderHeader,
    )
      ? this.calculateOrderInsightsAndSubmit(
          order?.orderHeader,
          order?.orderItems,
          exceptionMapByProductNum,
        )
      : this.handleOrderSubmission(
          order?.orderHeader,
          order?.orderItems,
          exceptionMapByProductNum,
        );
  }

  handleOrderSubmission(
    orderHeader: OrderHeader,
    orderItems: OrderItem[],
    exceptionMap: Map<number, MultiExceptionResolved[]>,
  ) {
    const finalOrder = {
      orderHeader,
      orderItems: this.finalizeOrderItemsBeforeSubmission(
        orderItems,
        exceptionMap,
      ),
    };

    return this.orderExceptionStoreService.resolveOrder(finalOrder);
  }

  finalizeOrderItemsBeforeSubmission(
    orderItems: OrderItem[],
    exceptionMapByProductNum: Map<number, MultiExceptionResolved[]>,
  ): OrderItem[] {
    const excludedProductNumbers = new Set();
    const productNumbersWithSequences = new Map();
    for (const item of orderItems) {
      productNumbersWithSequences.set(item?.productNumber, item?.sequence);
      //subbed out items with Unavailable/Restricted/OOS exceptions will be filtered out of the orderItems array
      if (!!item?.substituteFor) {
        const productExceptions =
          exceptionMapByProductNum.get(item?.substituteFor) || [];
        if (shouldRemoveSubbedOutOrderItem(productExceptions)) {
          excludedProductNumbers.add(item?.substituteFor);
        }
      }
    }
    return orderItems
      .map(item => {
        //updating the sequence on the subbed IN item to the sequence of the original item
        return excludedProductNumbers.has(item?.substituteFor)
          ? (item = {
              ...item,
              sequence: productNumbersWithSequences.get(item?.substituteFor),
            })
          : item;
      })
      .filter(item => {
        const hasNonZeroOrdered =
          (item?.eachesOrdered?.currentValue ?? 0) !== 0 ||
          (item?.unitsOrdered?.currentValue ?? 0) !== 0;
        return (
          (hasNonZeroOrdered || item?.dirtyFlag) &&
          !excludedProductNumbers.has(item?.productNumber)
        );
      });
  }

  calculateOrderInsightsAndSubmit(
    orderHeader: OrderHeader,
    orderItems: OrderItem[],
    exceptionMapByProductNum: Map<number, MultiExceptionResolved[]>,
  ) {
    const orderItemsWithoutReplacedItems =
      this.orderInsightsService.filterSubbedOrReplacedOrderItems(orderItems);

    this.orderInsightsService
      .orderItemsNotOnRecentlyPurchased$(
        orderItemsWithoutReplacedItems.filter(
          item =>
            (item?.eachesOrdered?.currentValue ?? 0) !== 0 ||
            (item?.unitsOrdered?.currentValue ?? 0) !== 0 ||
            (item?.eachesReserved ?? 0) !== 0 ||
            (item?.unitsReserved ?? 0) !== 0,
        ),
      )
      .pipe(take(1))
      .subscribe(newProductsOrderedNotInRecentlyPurchased => {
        const dyfProductsNotOrdered = new Set(
          orderHeader?.dyfProductsNotOrdered,
        );
        //remove subbed/replaced products from dyfNotOrdered array
        for (let item of orderItems) {
          if (dyfProductsNotOrdered.has(item.productNumber)) {
            dyfProductsNotOrdered.delete(item.productNumber);
          }
        }
        const finalOrderHeader = {
          ...orderHeader,
          newProductsOrderedNotInRecentlyPurchased,
          dyfProductsNotOrdered: Array.from(dyfProductsNotOrdered),
        };
        this.handleOrderSubmission(
          finalOrderHeader,
          orderItems,
          exceptionMapByProductNum,
        );
      });
  }

  navigateToSearchForSubs(
    productSummary: ProductSummary,
    exceptionId: string,
    orderId: string,
    searchText?: string,
  ) {
    this.toggleNewSearchService
      .isNewSearchEnabled()
      .pipe(take(1))
      .subscribe(newSearchEnabled => {
        if (newSearchEnabled) {
          this.productAlternativeSearchService.newSearchReplacementForException(
            productSummary,
            exceptionId,
            orderId,
            searchText,
          );
        } else {
          this.productAlternativeSearchService.searchReplacementForException(
            productSummary,
            exceptionId,
            orderId,
            searchText,
          );
        }
      });
  }

  getExceptionResolutionWithId(
    exceptionId: string,
  ): Observable<ExceptionResolution> {
    return this.store.select(selectExceptionResolutionWithId(exceptionId));
  }

  resolvepartialReserveFlag(data: EmittedPartialFlag) {
    const copyExceptionResolution = JSON.parse(
      JSON.stringify(data.exceptionResolution),
    );
    copyExceptionResolution.partialReserveFlag = data.flag;
    this.orderExceptionStoreService.resolveException(copyExceptionResolution);
  }

  flipSubstituteProduct(data: EmittedFlippedProductNumber) {
    const copyExceptionResolution: ExceptionResolution = JSON.parse(
      JSON.stringify(data.exceptionResolution),
    );
    copyExceptionResolution.flippedProductNumber = data.productNumber;
    this.orderExceptionStoreService.resolveException(copyExceptionResolution);
  }

  editRowClicked(data: ExceptionResolution) {
    const copyExceptionResolution = JSON.parse(JSON.stringify(data));
    copyExceptionResolution.expanded = true;
    copyExceptionResolution.flippedProductNumber =
      copyExceptionResolution.resolutionOrderItem?.substituteProduct?.productNumber;
    this.orderExceptionStoreService.resolveException(copyExceptionResolution);
  }

  cancelEditRowClicked(data: ExceptionResolution) {
    const exceptionResolution: ExceptionResolution = {
      ...data,
      expanded: false,
    };
    this.orderExceptionStoreService.resolveException(exceptionResolution);
  }

  navigateToCompareProductsPage(
    exceptionResolution: ExceptionResolution,
    orderId: string,
  ) {
    const items = [exceptionResolution.orderException.productNumber];
    exceptionResolution.orderException.subOrReplaceItems.map(value =>
      items.push(value.productNumber),
    );
    const productNumber = exceptionResolution.orderException.productNumber;
    this.navigationHelperService.routeTo(PATHS.SEARCH_COMPARE, {
      selectedProducts: items.join(','),
      originalItem: productNumber,
      mode: ProductCompareEnum.exceptionSubCompare,
      exceptionId:
        exceptionResolution.orderException.errorCode + ':' + productNumber,
      orderId: orderId,
    });
  }

  //#region private methods
  private resolveFullSubstitute(
    exceptionResolution: ExceptionResolution,
    subOrderItem: OrderItem,
    exceptionType: ExceptionType,
  ) {
    const resolvedOrderItem: ResolvedOrderItem = {
      originalProduct: {
        ...exceptionResolution.orderItem,
        unitsOrdered: JSON.parse(
          JSON.stringify(exceptionResolution.orderItem.unitsOrdered),
        ),
      },
      substituteProduct: {
        ...subOrderItem,
        substituteFor: exceptionResolution.orderItem?.productNumber,
        purchasedFromVendor: subOrderItem.purchasedFromVendor,
        vendorLeadTime: subOrderItem.vendorLeadTime,
        specialVendor: subOrderItem.specialVendor,
        dirtyFlag: true,
      },
    };
    switch (exceptionType) {
      case ExceptionType.PRODUCT_UNAVAILABLE:
        if (!!exceptionResolution.partialReserveFlag) {
          resolvedOrderItem.originalProduct.unitsOrdered.currentValue = 0;
          resolvedOrderItem.substituteProduct.substituteFlag =
            exceptionResolution.partialReserveFlag;
        } else {
          resolvedOrderItem.originalProduct.unitsOrdered.currentValue = 0;
          resolvedOrderItem.substituteProduct.substituteFlag = 'R';
        }
        break;
      case ExceptionType.RESTRICTED:
        resolvedOrderItem.originalProduct.unitsOrdered.currentValue = 0;
        resolvedOrderItem.substituteProduct.substituteFlag = 'S';
        break;
      case ExceptionType.OUT_OF_STOCK:
        if (!!exceptionResolution.partialReserveFlag) {
          resolvedOrderItem.substituteProduct.substituteFlag =
            exceptionResolution.partialReserveFlag;
          resolvedOrderItem.originalProduct.unitsOrdered.currentValue = 0;
        } else {
          resolvedOrderItem.substituteProduct.substituteFlag = 'B';
        }
        break;
      default:
        break;
    }
    return resolvedOrderItem;
  }

  private collapsedExceptionStatus(
    resolutionType: ResolutionType,
    exceptionType: ExceptionType,
  ): string {
    switch (resolutionType) {
      case ResolutionType.ACKNOWLEDGED:
        if (ExceptionType.PRODUCT_UNAVAILABLE === exceptionType) {
          return 'Reserved';
        } else {
          return 'Confirmed';
        }
      case ResolutionType.SUBSTITUTE:
        if (
          ExceptionType.RESTRICTED === exceptionType ||
          ExceptionType.PRODUCT_UNAVAILABLE === exceptionType
        ) {
          return 'Replaced';
        } else {
          return 'Substituted';
        }
      case ResolutionType.REMOVE:
        return 'Removed';
      case ResolutionType.ADD_TO_WAITLIST:
        return 'Added to Waitlist';
      case ResolutionType.WAIT_FOR_RESTOCK:
        return 'Waiting for Restock';
      case ResolutionType.PRICE:
        return 'Updated';
      default:
        return 'Confirmed';
    }
  }

  trackPageLoad(
    order: Order,
    productTracking: AnalyticsProduct[] | AnalyticsSubstituteProduct[],
  ): void {
    this.orderExceptionsTracingService.trackPageLoad(
      order.orderHeader.orderId,
      order.orderHeader.origOrderId,
      order.orderHeader.origSplitOrderId,
      order.orderHeader.totalPrice,
      order.orderHeader.tandemOrderNumber,
      order.orderHeader.requestedDeliveryDate,
      order.orderHeader.confirmedDeliveryDate,
    );
    this.orderExceptionsAnalyticsService.trackPageLoad(order, productTracking);
  }

  setIsConfirmationFlow(isConfirmationFlow: boolean) {
    this.orderExceptionStoreService.setIsConfirmationFlow(isConfirmationFlow);
  }
  isConfirmationFlow(): Observable<boolean> {
    return this.orderExceptionStoreService.isConfirmationFlow();
  }

  async isOrderExceptionStateInitialized(orderId: string) {
    const orderException = await this.orderExceptionStoreService
      .getOrderException()
      .pipe(take(1))
      .toPromise();

    const storedOrderId =
      !!orderException.order &&
      `${orderException.order.orderHeader.customerNumber}:${orderException.order.orderHeader.departmentNumber}:${orderException.order.orderHeader.orderId}`;

    return !!storedOrderId && storedOrderId === orderId;
  }

  public async confirmDeactivate(currentRoute: string, targetUrl: string) {
    if (this.platformService.platformType === PlatformEnum.desktop) {
      if (!targetUrl.includes('/products/')) {
        window.history.pushState(null, null, currentRoute);
      }

      this.modalService.setModalOptions(
        false,
        StayOnPageModalComponent,
        {
          title: this.translateService.instant(
            'i18n.helpTooltips.touch.ordering.modal.exceptionsTitle',
          ),
          buttonText: this.translateService.instant(
            'i18n.helpTooltips.touch.ordering.modal.button',
          ),
          exitButtonForTablet: true,
          exitButtonForMobile: true,
        },
        'simple-action-modal',
      );
    } else {
      this.modalService.setModalOptions(
        true,
        StayOnPageModalComponent,
        {
          title: this.translateService.instant(
            'i18n.helpTooltips.touch.ordering.modal.exceptionsTitle',
          ),
          buttonText: this.translateService.instant(
            'i18n.helpTooltips.touch.ordering.modal.button',
          ),
          exitButtonForTablet: true,
          exitButtonForMobile: true,
        },
        'simple-action-modal',
      );
    }

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    modal.onDidDismiss().then((resp: any) => {
      const confirmed = !!resp.data?.dispatchAction;

      if (confirmed) {
        this.clearOrderExceptions();
        this.setIsConfirmationFlow(false);
      }
      this.subject.next(confirmed);
    });

    return await modal.present();
  }
}
